<template>
  <div v-loading="loadingUniversities">
    <app-section-divider>
      <i class="el-icon-document"></i>
      <span style="padding: 0 10px">
        {{ $filters.capitalize("Education Info") }}
      </span>
      <el-button
        v-if="index > 0"
        @click="removeEducation(index)"
        icon="el-icon-delete"
        circle
        size="mini"
      />
    </app-section-divider>
    <el-form-item prop="organisation" label="College/University">
      <el-row justify="space-between">
        <el-col :span="22">
          <!--
            Was `el-select-v2`, which crashes under Vue 3.5: its OptionItem
            slot returns the result of `renderSlot` (a single Fragment vnode)
            straight into another `renderSlot`, and Vue's `ensureValidVNode`
            now calls `.some()` on it -> "vnodes.some is not a function".
            `el-select` is the stable component and needs no virtualisation
            here: the university list is fetched into the store in full, so
            `uniSearchMethod` filters locally and fits `filter-method`
            exactly — which also keeps the whole list visible before the
            user types, as `remote` would not have.
          -->
          <el-select
            v-if="universities && universities.length > 0"
            w-100
            v-model="form.organisation"
            @change="setValue"
            filterable
            :filter-method="uniSearchMethod"
            placeholder="EX: African Leadership University"
          >
            <el-option
              v-for="option in getAllOptions"
              :key="option.value"
              :value="option.value"
              :label="option.label"
            />
          </el-select>
        </el-col>
        <el-col :span="2">
          <AddUniComponent @created="createdUni($event)" />
        </el-col>
      </el-row>
    </el-form-item>
    <el-row :gutter="12">
      <!-- Degree -->
      <el-col :span="12">
        <el-form-item prop="degree" label="Degree">
          <el-select
            v-model="form.degree"
            @change="setValue"
            placeholder="Bachelor's"
            w-100
          >
            <el-option
              v-for="level in universityLevels"
              :value="level.name"
              :label="level.name"
              :key="`${level.name}-ind`"
            />
          </el-select>
        </el-form-item>
      </el-col>
      <!-- Program -->
      <el-col :span="12">
        <el-form-item prop="program" label="Program/Certification">
          <el-select
            placeholder="Comp Science"
            v-model="form.program"
            @change="setValue"
            w-100
          >
            <el-option
              v-for="program in relevantPrograms"
              :value="program"
              :label="program"
              :key="`${program}-ind`"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="12">
      <el-col :span="12">
        <el-form-item prop="From" label="Start Date">
          <el-date-picker
            type="month"
            format="MM/YYYY"
            placeholder="MM/YYYY"
            v-model="form.from"
            @change="setValue"
          />
        </el-form-item>
      </el-col>
      <el-col v-if="stillThere !== true" :span="12">
        <el-form-item prop="To" label="End Date">
          <el-date-picker
            type="month"
            format="MM/YYYY"
            placeholder="MM/YYYY"
            v-model="form.to"
            @change="setValue"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <div style="margin-bottom: 36px; margin-top: -10px">
          <el-checkbox
            @change="if (stillThere) form.to = undefined;"
            v-model="stillThere"
            label="Currently at the College/University."
            size="small"
          />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import {
  relevantPrograms,
  University,
  UniversityLevels,
  UserEducation,
} from "@/shared/interfaces";
import { Options, Vue } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import AddUniComponent from "./add-uni-dialog.vue";

@Options({
  name: "EducationInput",
  emits: ["update:modelValue"],
  mixins: [],
  components: {
    AddUniComponent,
  },
  computed: {},
  methods: {},
})
export default class extends Vue {
  universityLevels = UniversityLevels;
  relevantPrograms = relevantPrograms;

  query = "";

  @Prop({ type: Object })
  modelValue?: UserEducation;

  @Prop({})
  index!: number;

  removeEducation() {
    this.$emit("remove");
  }

  get universities() {
    return this.$store.state.modals.universitiesList;
  }
  get loadingUniversities() {
    return this.$store.state.modals.fetchingUniversities;
  }

  get getAllOptions() {
    if (this.query) return this.uniSearchOptions;
    else {
      return this.formulateOptions();
    }
  }

  uniSearchOptions: Array<{ value: string; label: string }> = [];

  formulateOptions() {
    return this.universities.map((uni, idx) => {
      return { value: uni._id, label: uni.name };
    });
  }

  /**
   * Searches for keywords in the uni
   * @param uni
   * @param query
   */
  searchString(uni: University, query: string): boolean {
    return query
      .toLowerCase()
      .split(" ")
      .map((str) => str.trim())
      .every((qWord) => uni.name.toLowerCase().includes(qWord));
  }
  /**
   * Searches for unis that satisfy the case
   * @search method
   */
  uniSearchMethod(query: string) {
    this.query = query;
    console.log("Searching uni ", query);
    if (query !== "") {
      this.uniSearchOptions = this.universities
        .filter((uni) => {
          return this.searchString(uni, query);
        })
        .map((uni, idx) => {
          return { value: uni._id, label: uni.name };
        });
    } else {
      this.uniSearchOptions = [];
    }
  }
  mounted() {
    console.log("Mounted the skill input element");
    this.assignForm();
    this.fetchUniversities();
  }
  // updated() {}

  form: UserEducation = {} as any;

  stillThere = false;

  assignForm() {
    this.form.organisation =
      typeof this.modelValue?.organisation === "string"
        ? this.modelValue?.organisation
        : (this.modelValue?.organisation?._id as any);
    this.form.from = this.modelValue?.from as any;
    this.form.to = this.modelValue?.to as any;
    this.form.program = this.modelValue?.program as any;
    this.form.degree = this.modelValue?.degree as any;
    this.form.description = this.modelValue?.description as any;
    if (this.form.from !== undefined && this.form.to == undefined) {
      this.stillThere = true;
    }
  }

  setValue() {
    this.$emit("update:modelValue", this.form);
  }

  fetchUniversities() {
    this.$store.dispatch("modals/fetchUniversities");
  }

  /**
   * When a uni is created
   */
  async createdUni(uni: University) {
    this.$store.dispatch("modals/addUniversity", uni);
    this.form.organisation = uni._id as any;
    this.setValue();
  }
}
</script>

<style lang="scss"></style>
